import React, { useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Button as ButtonAntd, Checkbox, Form } from 'antd'
import cn from 'classnames'
import { useTranslation } from 'lib/i18n'
import { useDebounce } from 'use-debounce'

import {
  VIDEO_REQUEST_INSTRUCTIONS,
  VIDEO_REQUEST_PRIVATE,
} from 'components/ProductPage/shared/FieldConstants'
import IsGift from 'components/ProductPage/shared/IsGift'
import VideoRequestFromName from 'components/ProductPage/shared/VideoRequestFromName'
import VideoRequestToName from 'components/ProductPage/shared/VideoRequestToName'
import RequestInstructions from 'components/RequestInstructions/RequestInstructions'
import { Button } from 'components/UI/Button/Button'
import { IconDelete, IconLongerInstruction } from 'components/UI/Icons'
import { IconAdd } from 'components/UI/Icons/IconAdd'
import Typography from 'components/UI/Typography/Typography'
import useDetectLanguageQuery from 'hooks/useDetectLanguage.gql'
import useLangMap from 'hooks/useLangMap'

import styles from './RequestAndInstructions.module.css'

const MAX_INSTRUCTIONS_COUNT = 250
const MAX_INSTRUCTIONS_COUNT_WITH_ADDON = 600

// This is equivalent to IsGift + ToName + FromName + Instructions
// Reorganized for checkout 2.0
// Can be removed after original got replaced as well
function ExtendedInstructionsAddon({
  extendedInstructionsActive,
  extendedInstructionsProduct,
  textTooLong,
  toggleExtendedInstructions,
}) {
  const { t } = useTranslation()

  const price = extendedInstructionsProduct.price.formattedValue

  return (
    <div
      className={cn(styles.instructions, {
        [styles.active]: extendedInstructionsActive,
      })}
    >
      {textTooLong && !extendedInstructionsActive && (
        <p className={styles.instructionsExtendMessage}>
          {t(
            'ExtendedInstructionsAddon.addToWriteLonger',
            'Getting a bit long there! Add more characters.',
          )}
        </p>
      )}

      <div className={styles.extendedInstructions}>
        <IconLongerInstruction className={styles.longerInstructionIcon} />
        <div className={styles.extendedInstructionsText}>
          <Typography.BodyBold>
            <strong>
              {t(
                'ExtendedInstructionsAddon.writeLongerInstructions',
                'Write longer instructions',
              )}
            </strong>
          </Typography.BodyBold>
          <Typography.BodySmall className={styles.subtitle}>
            {t(
              'ExtendedInstructionsAddon.writeLongerInstructionsSubtitle',
              'Write up to 600 characters in your instructions',
            )}
          </Typography.BodySmall>
          <Typography.BodySmallBold className={styles.price}>
            {price}
          </Typography.BodySmallBold>
        </div>

        {extendedInstructionsActive ? (
          <button
            type="button"
            onClick={toggleExtendedInstructions}
            data-cy="extendedInstructionsToggleButtonRemove"
            className={styles.remove}
          >
            <IconDelete className={styles.deleteIcon} />
          </button>
        ) : (
          <>
            <Button
              size="small"
              type="black"
              onClick={toggleExtendedInstructions}
              data-cy="extendedInstructionsToggleButtonAdd"
              id={styles.desktopAdd}
              className={cn(styles.desktopToggle, {
                [styles.addActive]: !extendedInstructionsActive,
              })}
            >
              {t('ExtendedInstructionsAddon.add1', 'Add')}
            </Button>
            <ButtonAntd
              onClick={toggleExtendedInstructions}
              className={styles.mobileToggle}
              shape="circle"
              data-cy="extendedInstructionsToggleButtonAdd"
              icon={<IconAdd data-cy="addIcon" />}
            />
          </>
        )}
      </div>
    </div>
  )
}

export const PrivateCheckbox = () => {
  const { t } = useTranslation()

  return (
    <Form.Item name={VIDEO_REQUEST_PRIVATE} valuePropName="checked">
      <Checkbox data-cy="videoPrivateByCustomerCheckbox">
        {t(
          'VideoRequestForm.unlistedCheckboxText',
          'Private video - do not list it on memmo',
        )}
      </Checkbox>
    </Form.Item>
  )
}

export const Instructions = ({
  profile,
  extendedInstructionsActive,
  extendedInstructionsProduct,
  toggleExtendedInstructions,
  profileProduct,
  showSavedIndicator,
  form,
}) => {
  const { t } = useTranslation()
  const maxInstructionsCount = extendedInstructionsActive
    ? MAX_INSTRUCTIONS_COUNT_WITH_ADDON
    : MAX_INSTRUCTIONS_COUNT

  const INPUT_MIN_LENGTH = 50
  const MIN_DETECT_LENGTH = 35

  useEffect(() => {
    const instructionsText = form.getFieldValue(VIDEO_REQUEST_INSTRUCTIONS)

    if (instructionsText && instructionsText.length > 0) {
      form.validateFields([VIDEO_REQUEST_INSTRUCTIONS])
    }
  }, [extendedInstructionsActive, form])

  const { getFullNameForLanguage } = useLangMap()

  const [detectLanguage, { data = {}, loading }] = useLazyQuery(
    useDetectLanguageQuery,
  )
  const [term, setTerm] = useState('')
  const [debouncedTerm] = useDebounce(term, 400)
  const [languageWarning, setLanguageWarning] = useState({})

  useEffect(() => {
    if (debouncedTerm?.length >= MIN_DETECT_LENGTH) {
      detectLanguage({
        variables: { text: debouncedTerm },
      })
    }
  }, [debouncedTerm, detectLanguage])

  const { name: profileName, id: profileId, languagesSpoken } = profile

  const initialValue = { id: [], label: [] }
  const profileLanguages =
    languagesSpoken?.reduce((acc, lgn) => {
      acc.id.push(lgn.id)
      acc.label.push(lgn.label)
      return acc
    }, initialValue) || initialValue

  const inputLanguage = (_rule, value) => {
    setTerm(value)
    const language = getFullNameForLanguage(profileLanguages.label)
    const detectedLang = data?.detectLanguage?.language

    if (
      detectedLang &&
      !loading &&
      profileLanguages.id.length &&
      term?.length >= INPUT_MIN_LENGTH &&
      !profileLanguages.id.includes(detectedLang)
    ) {
      if (value.length === 0) {
        setTerm('')
        return Promise.resolve()
      }

      setLanguageWarning({
        validateStatus: 'warning',
        help: t(
          'validators.languageDetector.unSupportedLanguageForTalent',
          'Please note that this talent only make videos in {{language}}',
          { language },
        ),
      })

      return Promise.resolve()
    }

    if (Object.keys(languageWarning).length) {
      setLanguageWarning({})
    }

    return Promise.resolve()
  }

  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue, setFieldsValue, validateFields, getFieldError }) => {
        const charactersUsed = getFieldValue(VIDEO_REQUEST_INSTRUCTIONS)
          ? getFieldValue(VIDEO_REQUEST_INSTRUCTIONS).length
          : 0
        const charactersLeft = maxInstructionsCount - charactersUsed

        const hasError = !!getFieldError(VIDEO_REQUEST_INSTRUCTIONS).length

        return (
          <>
            <Form.Item
              shouldUpdate
              name={VIDEO_REQUEST_INSTRUCTIONS}
              {...(hasError ? {} : languageWarning)}
              validateTrigger={['onChange', 'onSubmit']}
              rules={[
                {
                  required: true,
                  message: t(
                    'VideoRequestForm.instructionsRequired',
                    'You need to fill in the instructions',
                  ),
                },
                {
                  min: 20,
                  message: t(
                    'VideoRequestForm.instructionsTooShort',
                    'Great start! Add some more instructions (min 20 characters)',
                  ),
                },
                {
                  max: maxInstructionsCount,
                  message: t(
                    'VideoRequestForm.instructionsTooLong2',
                    'Your instructions are too long - keep it to below {{count}} characters',
                    { count: maxInstructionsCount },
                  ),
                },
                {
                  validator: inputLanguage,
                  validateTrigger: 'onChange',
                },
              ]}
              data-cy="instructions"
            >
              <RequestInstructions
                onChangeSuggestion={(newSuggestion) => {
                  // setFieldsValue object notation cannot handle values in nested format
                  const currentValue = getFieldValue('VIDEO_REQUEST')
                  const newValues = {
                    ...currentValue,
                    ...newSuggestion,
                  }
                  setFieldsValue({
                    VIDEO_REQUEST: newValues,
                  })

                  validateFields([VIDEO_REQUEST_INSTRUCTIONS]) // to trigger tracking
                }}
                onClearInstructions={() => {
                  const currentValue = getFieldValue('VIDEO_REQUEST')
                  setFieldsValue({
                    VIDEO_REQUEST: {
                      ...currentValue,
                      instructions: '',
                    },
                  })
                }}
                profileName={profileName}
                profileId={profileId}
                requestOccasionId={getFieldValue([
                  'VIDEO_REQUEST',
                  'requestOccasionId',
                ])}
                videoRequestDescriptionSuggestionId={getFieldValue([
                  'VIDEO_REQUEST',
                  'videoRequestDescriptionSuggestionId',
                ])}
                textareaStyle={{
                  minHeight: extendedInstructionsActive ? 260 : 160,
                  resize: 'none',
                }}
                profileProduct={profileProduct}
                charCount={t(
                  'VideoRequestForm.charactersRemaining',
                  '{{charCount}} characters remaining',
                  {
                    charCount: charactersLeft,
                  },
                )}
                showSavedIndicator={showSavedIndicator}
              />
            </Form.Item>
            {toggleExtendedInstructions && extendedInstructionsProduct && (
              <ExtendedInstructionsAddon
                textTooLong={charactersLeft < 0}
                extendedInstructionsProduct={extendedInstructionsProduct}
                extendedInstructionsActive={extendedInstructionsActive}
                toggleExtendedInstructions={toggleExtendedInstructions}
              />
            )}
          </>
        )
      }}
    </Form.Item>
  )
}

export default function RequestAndInstructions({
  profile,
  extendedInstructionsActive,
  extendedInstructionsProduct,
  toggleExtendedInstructions,
  profileProduct,
  showSavedIndicator,
  pronouns,
  form,
  hideTitle,
}) {
  const { t } = useTranslation()

  return (
    <div data-cy="requestFormSection" className={styles.requestInstructions}>
      {!hideTitle && (
        <div className={styles.requestTitleWrapper}>
          <Typography.H5 theme="primary">
            {t('orderDetails', 'Order Details')}
          </Typography.H5>
        </div>
      )}

      <IsGift variant="new" form={form} />

      <VideoRequestToName pronouns={pronouns} theme="dark" />

      <VideoRequestFromName pronouns={pronouns} theme="dark" />

      <Instructions
        profile={profile}
        extendedInstructionsActive={extendedInstructionsActive}
        extendedInstructionsProduct={extendedInstructionsProduct}
        toggleExtendedInstructions={toggleExtendedInstructions}
        profileProduct={profileProduct}
        showSavedIndicator={showSavedIndicator}
        form={form}
      />

      <PrivateCheckbox />
    </div>
  )
}
